/**
 * Funções utilitárias de formatação
 * Centralizadas para evitar duplicação de código
 */

/**
 * Rótulos que não identificam ninguém sozinhos: aparecem igual em prefeitura de
 * qualquer município. Não é lista de nomes proibidos — é vocabulário
 * administrativo da língua, que envelhece muito melhor que uma lista de órgãos.
 *
 * Siglas de UF entram porque "Diário Municipal dos Municípios (ES)" não vira
 * identificação por causa do "ES": o diário continua sendo de todos.
 */
const ROTULOS_GENERICOS = new Set([
  'prefeitura', 'municipal', 'municipais', 'municipio', 'municipios', 'camara',
  'unidade', 'unidades', 'unica', 'unico', 'diario', 'oficial', 'estado',
  'secretaria', 'governo', 'de', 'do', 'da', 'dos', 'das', 'e',
  'ac', 'al', 'am', 'ap', 'ba', 'ce', 'df', 'es', 'go', 'ma', 'mg', 'ms', 'mt',
  'pa', 'pb', 'pe', 'pi', 'pr', 'rj', 'rn', 'ro', 'rr', 'rs', 'sc', 'se', 'sp', 'to',
]);

/** Sem acento, sem caixa, sem pontuação — para comparar palavra com palavra. */
const semAcento = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * O nome carrega algum termo que identifique UMA entidade, e não um tipo de
 * entidade? "PREFEITURA MUNICIPAL" não; "PREFEITURA MUNICIPAL DE XANXERÊ" sim.
 */
export function identificaAlguem(nome: string | null | undefined): boolean {
  return semAcento(String(nome || ''))
    .split(/[^a-z0-9]+/)
    .some((t) => t.length > 0 && !ROTULOS_GENERICOS.has(t) && !/^\d+$/.test(t));
}

/**
 * Nome do órgão para as telas de licitação — workspace, monitoramento e disputa.
 *
 * `unidadeNome` VENCE, e continua vencendo: no federal a UASG é a informação
 * útil e o `orgaoNome` é a razão social genérica do CNPJ raiz. Medido na base:
 * "OBSERVATORIO NACIONAL - RJ" contra "MINISTERIO DA CIENCIA, TECNOLOGIA E
 * INOVACAO", "INSTITUTO NACIONAL DO CANCER" contra "MINISTERIO DA SAUDE".
 * Inverter a preferência quebraria a maior parte da base.
 *
 * A ÚNICA exceção é quando `unidadeNome` não identifica NINGUÉM — rótulo puro,
 * sem nome próprio nenhum. Aí ele perde para um `orgaoNome` que identifique. O
 * caso que motivou isto (BLL, PE 67/2026): unidade "PREFEITURA MUNICIPAL",
 * órgão "MUNICIPIO DE SAO SEBASTIAO". São cerca de 12 itens na base inteira.
 *
 * ⚠️ A exceção é ZERO identificadores, não "menos que o outro". Comparar
 * quantidade quebraria o federal: nomes de ministério são longos e ganhariam da
 * UASG na contagem. Basta a unidade ter um nome próprio qualquer para manter a
 * preferência, seja qual for o tamanho do órgão do outro lado.
 *
 * ⚠️ Esta saída NÃO é só exibição: `LicitacoesTab` a carimba no `title` da
 * proposta, que fica gravado. Título é snapshot — consertar aqui melhora os
 * novos e não toca nos 208 já gravados.
 */
export const getOrgaoDisplay = (item: any): string => {
  if (!item) return '';
  const unidade = item.unidadeNome || '';
  const orgao = item.orgaoNome || '';
  if (unidade && !identificaAlguem(unidade) && identificaAlguem(orgao)) return orgao;
  return unidade || orgao || '';
};

/**
 * Formata CNPJ para XX.XXX.XXX/XXXX-XX
 */
export const fmtCNPJ = (cnpj: string): string => {
  if (!cnpj) return '';
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

/**
 * Formata CPF para XXX.XXX.XXX-XX
 */
export const fmtCPF = (cpf: string): string => {
  if (!cpf) return '';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
};

/**
 * Formata telefone para (XX) XXXXX-XXXX ou (XX) XXXX-XXXX ou 0800 XXX XXXX
 */
export const fmtPhone = (phone: string): string => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');

  // 0800
  if (digits.startsWith('0800') && digits.length === 11) {
    return digits.replace(/^(\d{4})(\d{3})(\d{4})$/, '$1 $2 $3');
  }

  // Celular (11 dígitos)
  if (digits.length === 11) {
    return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }

  // Fixo (10 dígitos)
  if (digits.length === 10) {
    return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }

  return phone;
};

/**
 * Formata valor monetário para R$ X.XXX,XX
 */
/**
 * Competência 'YYYY-MM' → 'MM/YYYY'. Fatia a string: competência é mês civil,
 * não instante, e não pode passar por conversão de fuso.
 */
export const fmtCompetencia = (c: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(c || '').trim());
  return m ? `${m[2]}/${m[1]}` : '—';
};

const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** Competência 'YYYY-MM' → 'nov' (o mês por extenso curto, para rótulo como "rascunhos de nov"). Fatia a string. */
export const fmtMesCurto = (c: string | null | undefined): string => {
  const m = /^\d{4}-(\d{2})$/.exec(String(c || '').trim());
  return m ? MESES_CURTOS[Number(m[1]) - 1] ?? '—' : '—';
};

export const fmtCurrency = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 'R$ 0,00';
  return `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Formata número com casas decimais (sem R$)
 */
export const fmtNumber = (value: number | string, decimals: number = 2): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0,00';
  return num.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

/** Parseia string de data como local (sem offset UTC) */
export const parseLocalDate = (d: string): Date => {
  const [year, month, day] = d.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Decide se o valor é um DIA CIVIL (que não pode sofrer conversão de fuso) e, se for,
 * devolve as partes já fatiadas da string.
 *
 * Dois formatos contam como dia civil:
 *  1. `YYYY-MM-DD` — coluna `date` do Postgres (vencimento, competência, data-base).
 *  2. `YYYY-MM-DDT00:00:00[.000][Z]` — o mesmo dia civil serializado como meia-noite.
 *
 * Qualquer outro valor com hora é um INSTANTE e tem que ser convertido para BRT.
 *
 * Esta separação é o ponto central: até 29/07/2026 o teste era `/^\d{4}-\d{2}-\d{2}/`
 * SEM âncora de fim, então ele casava também com timestamp completo
 * (`2026-07-29T01:25:00.000Z`) e devolvia o pedaço de data **em UTC**, pulando a
 * conversão que o nome da função promete. Resultado: tudo que acontecia entre 21h e
 * 23h59 BRT era exibido com a data do dia seguinte — inclusive "Assinado em" de
 * documento oficial. Só ancorar a regex, porém, criaria o erro espelhado: o formato 2
 * acima passaria a recuar um dia. Daí os dois padrões explícitos.
 */
const civilDateParts = (d: any): { year: string; month: string; day: string } | null => {
  if (typeof d !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z?)?$/.exec(d.trim());
  return m ? { year: m[1], month: m[2], day: m[3] } : null;
};

/**
 * Converte para instante. Timestamp sem marcador de fuso é tratado como UTC, mesma
 * premissa que `fmtDateTime` já adota, para as duas funções não discordarem do mesmo
 * valor.
 */
const asInstant = (d: any): Date => {
  if (
    typeof d === 'string' &&
    /\d{2}:\d{2}/.test(d) &&
    !/Z$/.test(d) &&
    !/[+-]\d{2}:?\d{2}$/.test(d)
  ) {
    return new Date(d.replace(' ', 'T') + 'Z');
  }
  return new Date(d);
};

/** Formata data para DD/MM/YYYY (sempre BRT) */
export const fmtDate = (d: any): string => {
  if (!d) return '—';
  const civil = civilDateParts(d);
  if (civil) return `${civil.day}/${civil.month}/${civil.year}`;
  return asInstant(d).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

/** Formata data para DD/MM/YY (sempre BRT) */
export const fmtShortDate = (d: any): string => {
  if (!d) return '—';
  const civil = civilDateParts(d);
  if (civil) return `${civil.day}/${civil.month}/${civil.year.slice(-2)}`;
  return asInstant(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: 'America/Sao_Paulo' });
};

/** Formata data+hora para DD/MM/YYYY, HH:MM (sempre BRT) */
export const fmtDateTime = (d: any): string => {
  if (!d) return '—';
  // Usa o mesmo `asInstant` do fmtDate para que as duas funções nunca discordem sobre o
  // mesmo valor (era daqui que vinha a premissa de "sem fuso = UTC").
  return asInstant(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
};

/**
 * Data+hora de PAREDE: DD/MM/YYYY, HH:MM, SEM conversão de fuso. Para coluna `timestamp` sem fuso que o backend grava com a
 * hora de Brasília — medido em `procurement_proposals.submittedAt` (26/10/2026: 21:23 no banco = 21:23 em BRT, conferido
 * contra a trilha do robô; o `updatedAt` da mesma tabela é UTC). O `fmtDateTime` a lia como UTC e mostrava 18:23. O fuso
 * que vier na string (o "Z" da serialização) é ignorado DE PROPÓSITO. ⛔ Só use em coluna medida assim.
 */
export const fmtDateTimeDeParede = (d: any): string => {
  if (!d) return '—';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return fmtDateTime(d);
  return `${m[3]}/${m[2]}/${m[1]}, ${m[4]}:${m[5]}`;
};

/**
 * Formata só a hora, HH:MM (sempre BRT). Recebe INSTANTE já resolvido (ms ou Date),
 * não string: o `dataHoraFimContagem` do ComprasNet vem sem fuso e em hora de
 * Brasília, e passar a string pelo `asInstant` a leria como UTC (3h de erro).
 */
export const fmtTime = (d: number | Date | null | undefined): string => {
  if (d == null) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  if (!isFinite(dt.getTime())) return '—';
  return dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
};

/** A hora com segundos, HH:MM:SS (sempre BRT), de um INSTANTE (ms ou Date). Para "atualizado às", onde o minuto não basta. */
export const fmtTimeWithSeconds = (d: number | Date | null | undefined): string => {
  if (d == null) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  if (!isFinite(dt.getTime())) return '—';
  return dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'America/Sao_Paulo' });
};

/** Mapeia o `modoDisputa` (string vinda do PNCP/portal) pra um chip compacto.
 *  Aberto-Fechado é o caso que mais importa de identificar (3 fases distintas
 *  na disputa); Fechado raro mas existe; Dispensa Com Disputa diferencia
 *  dispensa "com lances" de "só propostas". Retorna null pra "Não se aplica"
 *  ou modo vazio. */
export const getModoDisputaBadge = (modo?: string | null): { short: string; full: string; color: string } | null => {
  if (!modo) return null;
  const m = modo.toLowerCase();
  if (m.includes('aberto-fechado') || m.includes('aberto/fechado')) {
    return { short: 'A-F', full: 'Aberto-Fechado', color: 'bg-purple-500/15 text-purple-300' };
  }
  if (m.includes('fechado-aberto')) {
    return { short: 'F-A', full: 'Fechado-Aberto', color: 'bg-fuchsia-500/15 text-fuchsia-300' };
  }
  if (m.includes('aberto')) {
    return { short: 'Aberto', full: 'Aberto', color: 'bg-blue-500/15 text-blue-300' };
  }
  if (m.includes('fechado')) {
    return { short: 'Fechado', full: 'Fechado', color: 'bg-orange-500/15 text-orange-300' };
  }
  if (m.includes('dispensa com disputa') || m.includes('com disputa')) {
    return { short: 'C/disputa', full: 'Dispensa Com Disputa', color: 'bg-emerald-500/15 text-emerald-300' };
  }
  if (m.includes('não se aplica') || m.includes('nao se aplica')) {
    return null;
  }
  return { short: modo.length > 12 ? modo.slice(0, 12) + '…' : modo, full: modo, color: 'bg-slate-600/40 text-slate-300' };
};

/** Caracteriza a ESTRUTURA da disputa (grupo vs item) a partir do que está
 *  disponível na proposta + item. Hierarquia de fontes:
 *    1. proposal.proposalItems[].groupNumber (populado pós-scrape do portal — confiável)
 *    2. item.detailsCache.itens (PNCP — só count, não distingue grupo)
 *  Quando a estrutura de grupo é desconhecida (pré-scrape), retorna só o count. */
export const getStructureBadge = (
  p: { proposalItems?: any[]; item?: { detailsCache?: { itens?: any[] } | null } | null } | null | undefined,
): { short: string; full: string; color: string; known: boolean } | null => {
  if (!p) return null;
  const scraped = Array.isArray(p.proposalItems) ? p.proposalItems : [];
  if (scraped.length > 0) {
    const grouped = scraped.filter((i: any) => i.groupNumber != null);
    const ungrouped = scraped.filter((i: any) => i.groupNumber == null);
    const groupIds = new Set(grouped.map((i: any) => String(i.groupNumber)));
    if (groupIds.size === 1 && ungrouped.length === 0) {
      return { short: `Grupo c/ ${grouped.length} itens`, full: `1 grupo com ${grouped.length} itens — 1 lance no total`, color: 'bg-amber-500/15 text-amber-300 border-amber-700/40', known: true };
    }
    if (groupIds.size > 1 && ungrouped.length === 0) {
      return { short: `${groupIds.size} Grupos (${scraped.length} itens)`, full: `${groupIds.size} grupos com ${scraped.length} itens`, color: 'bg-amber-500/15 text-amber-300 border-amber-700/40', known: true };
    }
    if (groupIds.size > 0 && ungrouped.length > 0) {
      return { short: `Grupo + ${ungrouped.length} Itens`, full: `Misto: ${grouped.length} no grupo + ${ungrouped.length} soltos`, color: 'bg-amber-500/15 text-amber-300 border-amber-700/40', known: true };
    }
    if (scraped.length === 1) {
      return { short: 'Item único', full: 'Item único — 1 lance', color: 'bg-slate-500/15 text-slate-300 border-slate-600/40', known: true };
    }
    return { short: `${scraped.length} Itens`, full: `${scraped.length} itens independentes — N lances`, color: 'bg-cyan-500/15 text-cyan-300 border-cyan-700/40', known: true };
  }
  const detItens = p.item?.detailsCache?.itens;
  if (Array.isArray(detItens) && detItens.length > 0) {
    if (detItens.length === 1) {
      return { short: 'Item único', full: 'Item único (PNCP)', color: 'bg-slate-500/15 text-slate-300 border-slate-600/40', known: true };
    }
    return { short: `${detItens.length} Itens`, full: `${detItens.length} itens — estrutura de grupo só após cadastrar a proposta`, color: 'bg-slate-500/15 text-slate-400 border-slate-600/40 italic', known: false };
  }
  return null;
};

/** Detecta se uma disputa é modo Aberto/Fechado consultando AMBAS as fontes:
 *  metadata persistida (item.modoDisputa, participacao.modoDisputa) e fase ao
 *  vivo do rankingData.itensGrupo/emDisputa. Helper unificado pra evitar
 *  divergência entre DisputeSessionCard (badge), DisputeExpandedHeader (chip),
 *  LanceFechadoModal (trigger) e DisputeView (preview).
 *
 *  ModoDisputaCompraEnum oficial (HAR 27/05/26 UFSM 90013):
 *    A=Aberto, F=Fechado, AF=Aberto/Fechado, FA=Fechado/Aberto, D=Dispensa.
 *  Legado: '2' do PNCP antigo = Aberto-Fechado.
 *
 *  FaseItemEnum em runtime (rankingData.itensGrupo[-1][0].fase):
 *    AF=Aguardando fase fechada, LF=Etapa fechada (durante).
 */
/**
 * F-4 (Onda 2, 08/08/2026 — regra INDELÉVEL do repo): `modalidadeId` cru NÃO é
 * confiável (ComprasNet 6=Dispensa, PNCP 6=PREGÃO). Ordem de decisão:
 *  1. `participacao.modalidade` (enum do PRÓPRIO portal, confiável: 5=Pregão, 6/8=Dispensa)
 *  2. `modalidadeNome` textual (regex)
 *  3. `modalidadeId` como último recurso (convenção ComprasNet)
 * Antes, 3 pontos do DisputeView decidiam pelo id cru — um Pregão-PNCP (id 6)
 * era tratado como Dispensa: pré-preenchia fim +6h e salvava sniping (robô mudo
 * esperando "os últimos 120s" de uma janela que prorroga).
 */
export const resolveModalidade = (
  item?: { modalidadeId?: any; modalidadeNome?: string } | null,
  participacaoModalidade?: any,
): 'pregao' | 'dispensa' | 'outra' | 'desconhecida' => {
  const pm = Number(participacaoModalidade) || 0;
  if (pm === 5) return 'pregao';
  if (pm === 6 || pm === 8) return 'dispensa';
  const nome = String(item?.modalidadeNome || '').toLowerCase();
  if (/preg/.test(nome)) return 'pregao';
  if (/dispens/.test(nome)) return 'dispensa';
  if (nome.trim()) return 'outra';
  const id = Number(item?.modalidadeId) || 0;
  if (id === 5) return 'pregao';
  if (id === 6 || id === 8) return 'dispensa';
  return 'desconhecida';
};

export const isModoAbertoFechado = (item: any, rankingData?: any): boolean => {
  // Fonte 1: metadata (participacao.modoDisputa OU item.modoDisputa)
  const fromMeta = String(rankingData?.participacao?.modoDisputa ?? item?.modoDisputa ?? '');
  if (fromMeta === 'AF' || fromMeta === '2') return true;
  const modoNome = String(item?.modoDisputaNome ?? item?.modoDisputa ?? '');
  if (/aberto.?fechado/i.test(modoNome)) return true;
  // Fonte 2: fase ao vivo (LF ou AF no rankingData)
  const igRaw = rankingData?.itensGrupo?.['-1'] || rankingData?.emDisputa;
  const rows = Array.isArray(igRaw) ? igRaw : (igRaw?.registros || igRaw?.itens || igRaw?.items || []);
  const fase = rows[0]?.fase;
  if (fase === 'LF' || fase === 'AF') return true;
  return false;
};

/** Normaliza `rankingData.todasPropostas` em uma lista única de propostas
 *  com shape { participante, classificacao, valores, ... }. O extension
 *  pode gravar em 3 shapes (HAR 27/05/26 UFSM 90013):
 *    A) `{'-1': { items: [propostas...] }}` — grupo agregado normalizado
 *    B) `{'-1': [{ numero:-1, propostasItem: [propostas...] }]}` — grupo direto da API
 *    C) `{'1': [{ numero:-1, propostasItem: [...] }], '2': [...], ...}` — replicado por item (Pregão por grupo)
 *  Tenta '-1' primeiro; se vazio, varre as chaves numéricas até achar payload.
 *  Em Pregão por grupo (caso C), todas as keys têm o mesmo conteúdo. */
export const getFinalRankingRows = (rankingData: any): any[] => {
  const tp = rankingData?.todasPropostas;
  if (!tp || typeof tp !== 'object') return [];
  const orderedKeys = ['-1', ...Object.keys(tp).filter((k) => k !== '-1')];
  for (const k of orderedKeys) {
    const entry = tp[k];
    if (!entry) continue;
    // Shape A: { items: [...] }
    if (Array.isArray(entry.items) && entry.items.length > 0) {
      const inner = entry.items[0];
      if (inner && Array.isArray(inner.propostasItem) && inner.propostasItem.length > 0) {
        return inner.propostasItem;
      }
      return entry.items;
    }
    // Shape B/C: array direto
    if (Array.isArray(entry) && entry.length > 0) {
      const inner = entry[0];
      if (inner && Array.isArray(inner.propostasItem) && inner.propostasItem.length > 0) {
        return inner.propostasItem;
      }
      return entry;
    }
  }
  return [];
};

/** CNPJ da IPCOM (sem máscara) — usado pra localizar nossa proposta no ranking. */
export const IPCOM_CNPJ = '06163889000104';

export type IpcomOutcome = {
  found: boolean; // IPCOM está no ranking coletado?
  classif: number | null; // posição (1-based)
  derived: boolean; // posição foi derivada por valor (classificacao vinha vazia)
  total: number; // total de participantes
  isWinner: boolean; // classif === 1
};

/** FONTE ÚNICA de posição/outcome da IPCOM numa disputa. Lê o ranking canônico
 *  (getFinalRankingRows → rankingData.todasPropostas) que ComprasNet, BLL e PCP
 *  populam no MESMO formato, e deriva a posição por VALOR quando o portal não
 *  preencheu `classificacao` (ex.: ComprasNet deixa o campo vazio quando a
 *  IPCOM não foi convocada pra fase fechada — mas a posição existe no ranking).
 *  Use isto em todos os componentes (header, card, lista) pra evitar que cada
 *  um leia de uma fonte diferente e mostre resultados inconsistentes. */
const outcomeFromRows = (rows: any[]): IpcomOutcome => {
  const total = rows.length;
  const norm = (r: any) => String(r?.participante?.identificacao || '').replace(/\D/g, '');
  const mine = rows.find((r: any) => norm(r) === IPCOM_CNPJ);
  if (!mine) return { found: false, classif: null, derived: false, total, isWinner: false };
  const raw = mine.classificacao;
  let classif: number | null = raw != null && raw !== '' ? Number(raw) : null;
  let derived = false;
  if (classif == null && total > 0) {
    const valFinal = (r: any) =>
      Number(
        r?.valores?.valorPropostaInicialOuLances?.valorCalculado?.valorTotal ??
          r?.valores?.valorPropostaInicial?.valorCalculado?.valorTotal ??
          Infinity,
      );
    const ordered = [...rows].sort((a, b) => valFinal(a) - valFinal(b));
    const idx = ordered.findIndex((r: any) => norm(r) === IPCOM_CNPJ);
    if (idx >= 0) {
      classif = idx + 1;
      derived = true;
    }
  }
  return { found: true, classif, derived, total, isWinner: classif === 1 };
};

export const computeIpcomOutcome = (rankingData: any): IpcomOutcome =>
  outcomeFromRows(getFinalRankingRows(rankingData));

/** Outcome POR ITEM (disputa por item: dispensa/pregão item). Posição da IPCOM
 *  em CADA item separadamente — cada item tem ranking/vencedor próprios. Vazio
 *  pra GRUPO (julgado pelo total → use computeIpcomOutcome). */
export type IpcomOutcomeItem = IpcomOutcome & { itemNumber: number };
export const computeIpcomOutcomeByItem = (rankingData: any): IpcomOutcomeItem[] => {
  const tp = rankingData?.todasPropostas;
  if (!tp || typeof tp !== 'object') return [];
  const unwrap = (entry: any): any[] => {
    if (!entry) return [];
    const arr: any[] = Array.isArray(entry) ? entry : Array.isArray(entry.items) ? entry.items : [];
    if (arr.length && Array.isArray(arr[0]?.propostasItem)) return arr[0].propostasItem;
    return arr;
  };
  return Object.keys(tp)
    .filter((k) => k !== '-1') // '-1' = grupo agregado, não é por item
    .map((k) => ({ k, rows: unwrap(tp[k]) }))
    .filter((x) => x.rows.length > 0)
    .sort((a, b) => Number(a.k) - Number(b.k))
    .map((x) => ({ itemNumber: Number(x.k), ...outcomeFromRows(x.rows) }));
};

/** Deriva a FASE atual de um item monitorado, combinando status + datas + session.
 *  Substitui o status cru por algo visualmente legível ("Em disputa", "Aguardando
 *  abertura", "Aguardando julgamento", "Homologada") e separa terminais. */
export type MonitoringPhase = {
  key: 'aguardando_abertura' | 'em_disputa' | 'aguardando_julgamento' | 'suspensa' | 'desistida' | 'homologada' | 'encerrada' | 'revogada' | 'fracassada' | 'sem_decisao';
  label: string;
  color: string;
  isTerminal: boolean;
};
// `color` é o SELO da fase (só o Monitorar usa). Padrão da Central (L3, 23/10/2026): andamento e fato consumado são
// neutros; a cor fica em Em disputa (acontecendo agora), Suspensa e Revogada.
export const getMonitoringPhase = (item: any): MonitoringPhase => {
  const status = String(item?.status || '').toLowerCase();
  const now = Date.now();
  const ab = item?.dataAbertura ? new Date(item.dataAbertura).getTime() : null;
  const enc = item?.dataEncerramento ? new Date(item.dataEncerramento).getTime() : null;
  // Terminais primeiro — independem das datas
  if (status === 'homologado' || status === 'adjudicado' || status === 'ganho') {
    return { key: 'homologada', label: 'Homologada', color: 'bg-slate-600/30 text-slate-300 border-slate-600/40', isTerminal: true };
  }
  if (status === 'perdido') {
    // Perder o lance NÃO encerra a licitação: na habilitação o pregoeiro pode
    // convocar o 2º/3º. Se ainda está monitorado, fica em Ativas (aguardando
    // julgamento); senão, vira histórico. Consistente com o backend (28/05/26).
    if (item?.monitored) {
      return { key: 'aguardando_julgamento', label: 'Aguardando julgamento', color: 'bg-slate-600/30 text-slate-300 border-slate-600/40', isTerminal: false };
    }
    return { key: 'encerrada', label: 'Encerrada', color: 'bg-slate-500/25 text-slate-300 border-slate-600/40', isTerminal: true };
  }
  if (status === 'encerrada') {
    return { key: 'encerrada', label: 'Encerrada', color: 'bg-slate-500/25 text-slate-300 border-slate-600/40', isTerminal: true };
  }
  if (status === 'revogada') {
    return { key: 'revogada', label: 'Revogada', color: 'bg-red-500/20 text-red-300 border-red-700/40', isTerminal: true };
  }
  if (status === 'fracassada') {
    return { key: 'fracassada', label: 'Fracassada', color: 'bg-slate-500/25 text-slate-400 border-slate-600/40', isTerminal: true };
  }
  if (status === 'desistida') {
    return { key: 'desistida', label: 'Desistida', color: 'bg-slate-600/30 text-slate-300 border-slate-600/40', isTerminal: true };
  }
  // Estado especial — exige atenção do user
  if (status === 'suspensa') {
    return { key: 'suspensa', label: 'Suspensa', color: 'bg-amber-500/20 text-amber-300 border-amber-700/40', isTerminal: false };
  }
  // Aguardando julgamento (já passou pela disputa)
  if (status === 'aguardando_julgamento') {
    return { key: 'aguardando_julgamento', label: 'Aguardando julgamento', color: 'bg-slate-600/30 text-slate-300 border-slate-600/40', isTerminal: false };
  }
  // Fase derivada por TEMPO pros participando/analisando
  const inFlight = status === 'participando' || status === 'analisando' || status === 'interessado';
  if (inFlight) {
    if (ab && now < ab) {
      return { key: 'aguardando_abertura', label: 'Aguardando abertura', color: 'bg-slate-600/30 text-slate-300 border-slate-600/40', isTerminal: false };
    }
    if (enc && now > enc) {
      // disputa já passou mas status ainda não promoveu — provavelmente o portal
      // não atualizou; mostrar como aguardando julgamento (fase real)
      return { key: 'aguardando_julgamento', label: 'Aguardando julgamento', color: 'bg-slate-600/30 text-slate-300 border-slate-600/40', isTerminal: false };
    }
    return { key: 'em_disputa', label: 'Em disputa', color: 'bg-amber-500/20 text-amber-300 border-amber-700/40', isTerminal: false };
  }
  // novo / sem decisão
  return { key: 'sem_decisao', label: 'Sem decisão', color: 'bg-slate-600/40 text-slate-400 border-slate-600/40', isTerminal: false };
};

/** Formato relativo curto: "agora", "há 5min", "há 2h", "há 3d". Útil pra timestamps de scraping/refresh. */
export const fmtRelativeTime = (d: any): string => {
  if (!d) return '';
  let input = d;
  if (typeof input === 'string' && /\d{2}:\d{2}/.test(input) && !/Z$/.test(input) && !/[+-]\d{2}:?\d{2}$/.test(input)) {
    input = input.replace(' ', 'T') + 'Z';
  }
  const target = new Date(input).getTime();
  if (isNaN(target)) return '';
  const diffSec = Math.floor((Date.now() - target) / 1000);
  if (diffSec < 0) return 'agora';
  if (diffSec < 30) return 'agora';
  if (diffSec < 60) return `há ${diffSec}s`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `há ${diffMin}min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `há ${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `há ${diffDay}d`;
  const diffMon = Math.floor(diffDay / 30);
  return `há ${diffMon}m`;
};

/** Calcula dias até uma data (positivo=futuro, negativo=passado) */
export const getDaysUntil = (d: any): number | null => {
  if (!d) return null;
  let target: Date;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) {
    const [year, month, day] = d.split('T')[0].split('-').map(Number);
    target = new Date(year, month - 1, day);
  } else {
    target = new Date(d);
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * O prazo em palavras, a partir dos dias do getDaysUntil (09/10/2026): "em 10 dias", "vence hoje", "vencido há 3 dias",
 * "vencido há 2 meses", "vencido há 6 anos". Para o selo NEUTRO de prazo: doze "Vencido!" vermelhos iguais eram
 * papel de parede, e "há 6 anos" diz o que importa sem cor. Ano = 365 dias, mês = 30 (é ordem de grandeza, não data).
 */
export const fmtPrazo = (dias: number | null): string => {
  if (dias === null || Number.isNaN(dias)) return '—';
  const n = (q: number, um: string, varios: string) => `${q} ${q === 1 ? um : varios}`;
  if (dias > 0) return `em ${n(dias, 'dia', 'dias')}`;
  if (dias === 0) return 'vence hoje';
  const atraso = -dias;
  if (atraso >= 365) return `vencido há ${n(Math.floor(atraso / 365), 'ano', 'anos')}`;
  if (atraso >= 30) return `vencido há ${n(Math.floor(atraso / 30), 'mês', 'meses')}`;
  return `vencido há ${n(atraso, 'dia', 'dias')}`;
};

/**
 * Detecta a última PRORROGAÇÃO no histórico de encerramento de uma licitação
 * (entrada em que a data nova foi empurrada PRA FRENTE). Retorna { from, to }
 * (strings ISO) ou null. Usado pro badge "⏱ Prorrogada" + tooltip com as datas.
 */
export const getProrrogacao = (item: any): { from: string; to: string } | null => {
  const hist = Array.isArray(item?.dataEncerramentoHistory) ? item.dataEncerramentoHistory : [];
  let last: { from: string; to: string } | null = null;
  for (const h of hist) {
    if (h?.previousDate && h?.newDate && new Date(h.newDate).getTime() > new Date(h.previousDate).getTime()) {
      last = { from: h.previousDate, to: h.newDate };
    }
  }
  return last;
};

/**
 * Licitação "encerrada"/passada: o ÚLTIMO evento relevante (encerramento de
 * proposta OU sessão) já passou. Usado pra esconder badges contextuais
 * (ex: "Prorrogada") nas que já fecharam.
 */
export const isLicitacaoOver = (item: any): boolean => {
  const now = Date.now();
  const ds = [item?.dataEncerramento, item?.dataSessaoInicio, item?.dataSessaoFim]
    .filter(Boolean)
    .map((d: any) => new Date(d).getTime())
    .filter((t: number) => Number.isFinite(t));
  if (!ds.length) return false;
  return Math.max(...ds) < now;
};

/**
 * Quanto tempo depois do INÍCIO da sessão a janela ainda é considerada aberta
 * quando o portal não informou o fim. Generoso de propósito: pregão em Modo
 * Aberto prorroga a cada lance e pode varar a manhã (o INCA de 15/09 abriu
 * 08:59 e o portal dizia "abertura de itens até 18:00"). O que precisa fechar
 * é a sessão de ONTEM, não a de daqui a uma hora.
 */
export const JANELA_DISPUTA_GRACA_MS = 12 * 60 * 60 * 1000;

/**
 * A janela da disputa já fechou?
 *
 * ⛔ NÃO confundir com `isLicitacaoOver` (15/09/2026). Aquela responde "esta
 * licitação está no passado?" e serve pra esconder badge contextual — para isso
 * somar `dataSessaoInicio` entre os "eventos finais" é aceitável. Para decidir
 * se ainda dá pra INICIAR o robô é errado, porque trata o COMEÇO da sessão como
 * se fosse o fim dela.
 *
 * Custou duas licitações no mesmo dia. O ComprasNet só expõe
 * `dataHoraFimDisputa` depois que a disputa é agendada (ver skill
 * comprasnet-fetch), então em pregão o item chega com `dataSessaoFim` NULL e
 * `dataEncerramento` = `dataSessaoInicio` = o horário de abertura. No minuto
 * seguinte a esse horário o `Math.max(...) < now` virava true:
 *
 *   INCA PE 354/2026     sessão 08:59, às 09:00 o card virou "Não disputada"
 *   São Vicente PE 5/2026 sessão 10:00, às 10:11 idem — e a disputa rodando
 *
 * Ou seja: o botão sumia exatamente no instante em que ele passava a ser útil.
 * Nos dois casos o conserto foi preencher `dataSessaoFim` na unha no banco.
 *
 * A regra aqui usa a melhor fonte disponível, nessa ordem:
 *   1. `dataSessaoFim` — autoritativo quando o portal informou;
 *   2. `dataSessaoInicio` — começou: só fecha depois da carência;
 *   3. `dataEncerramento` — sem nada sobre a sessão, o prazo da proposta + carência.
 * Sem data nenhuma, não dá pra afirmar que fechou → false (mesma escolha de
 * `isLicitacaoOver`). Quem chama ainda combina isto com o fim de contagem
 * conhecido, que é mais preciso quando existe.
 */
export const isJanelaDisputaFechada = (item: any, agora: number = Date.now()): boolean => {
  const ts = (d: any): number | null => {
    if (!d) return null;
    const t = new Date(d).getTime();
    return Number.isFinite(t) ? t : null;
  };
  const fim = ts(item?.dataSessaoFim);
  if (fim != null) return fim < agora;
  const inicio = ts(item?.dataSessaoInicio);
  if (inicio != null) return agora - inicio > JANELA_DISPUTA_GRACA_MS;
  const encerramento = ts(item?.dataEncerramento);
  if (encerramento != null) return agora - encerramento > JANELA_DISPUTA_GRACA_MS;
  return false;
};

/**
 * Máscara de CNPJ para input (adiciona pontuação enquanto digita)
 */
export const maskCNPJ = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

/**
 * Máscara de CPF para input
 */
export const maskCPF = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

/**
 * Valida CPF pelos dígitos verificadores (aceita com ou sem máscara).
 */
export const isValidCpf = (value: string | null | undefined): boolean => {
  const d = (value || '').replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const dv = (base: string, factor: number): number => {
    let sum = 0;
    for (let i = 0; i < base.length; i++) sum += parseInt(base[i], 10) * (factor - i);
    const r = (sum * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return dv(d.slice(0, 9), 10) === +d[9] && dv(d.slice(0, 10), 11) === +d[10];
};

/**
 * Máscara de telefone para input
 */
export const maskPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
};

/** Retorna data local atual como YYYY-MM-DD (seguro para qualquer horário BRT) */
export const getTodayStr = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/** Soma N anos a uma data YYYY-MM-DD e retorna YYYY-MM-DD (sem conversão UTC).
 *  29/02 em ano não bissexto cai para 28/02. */
export const addYearsStr = (dateStr: string, years = 1): string => {
  if (!dateStr) return '';
  const [y, m, d] = String(dateStr).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return '';
  const lastDay = new Date(y + years, m, 0).getUTCDate();
  return `${y + years}-${String(m).padStart(2, '0')}-${String(Math.min(d, lastDay)).padStart(2, '0')}`;
};

/** Soma N meses a uma data YYYY-MM-DD e retorna YYYY-MM-DD (sem conversão UTC).
 *  Preserva o dia do mês, com clamp no último dia do mês de destino:
 *  31/01 + 1 mês = 28/02. Irmão de `addYearsStr`.
 *
 *  Usado no cronograma de operações de crédito, onde o slot n vence em
 *  `startDate` + (n − 1) meses. Aritmética de calendário, não formatação —
 *  para EXIBIR o resultado, passe por `fmtDate`. */
export const addMonthsStr = (dateStr: string, months: number): string => {
  if (!dateStr) return '';
  const [y, m, d] = String(dateStr).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return '';
  const totalMonths = (y * 12) + (m - 1) + months;
  const ty = Math.floor(totalMonths / 12);
  const tm = (totalMonths % 12) + 1;
  const lastDay = new Date(ty, tm, 0).getDate();
  return `${ty}-${String(tm).padStart(2, '0')}-${String(Math.min(d, lastDay)).padStart(2, '0')}`;
};

/** Retorna mês local atual como YYYY-MM (seguro para qualquer horário BRT) */
export const getCurrentMonthStr = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/** Retorna data local deslocada N dias como YYYY-MM-DD */
export const getOffsetDateStr = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Retorna último dia do mês dado (year, month 1-based) como YYYY-MM-DD */
export const getLastDayOfMonth = (year: number, month: number): string => {
  const d = new Date(year, month, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/* ========== Month / Period Helpers (shared across tabs) ========== */

export const monthNames = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/** Navigate YYYY-MM by delta months */
export const navigateMonth = (current: string, delta: number): string => {
  const fallback = new Date();
  const [y, m] = current ? current.split('-').map(Number) : [fallback.getFullYear(), fallback.getMonth() + 1];
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** Format month(s) label for display */
export const formatMonthLabel = (value: string | string[]): string => {
  const arr = Array.isArray(value) ? value : value ? [value] : [];
  if (arr.length === 0) return 'Todos os meses';
  const sorted = [...arr].sort();
  if (sorted.length === 1) {
    const [y, m] = sorted[0].split('-').map(Number);
    return `${monthNames[m - 1]} ${y}`;
  }
  const first = sorted[0].split('-').map(Number);
  const last = sorted[sorted.length - 1].split('-').map(Number);
  const fLabel = `${monthNames[first[1] - 1].slice(0, 3)}/${first[0]}`;
  const lLabel = `${monthNames[last[1] - 1].slice(0, 3)}/${last[0]}`;
  return `${fLabel} - ${lLabel}`;
};

/** Format date range label DD/MM - DD/MM/YYYY */
export const formatPeriodLabel = (startDate: string, endDate: string): string => {
  if (!startDate && !endDate) return 'Selecionar periodo';
  const fmtPart = (d: string) => {
    const [y, m, day] = d.split('-');
    return { day, month: m, year: y };
  };
  if (startDate && endDate) {
    const s = fmtPart(startDate);
    const e = fmtPart(endDate);
    if (startDate === endDate) return `${s.day}/${s.month}/${s.year}`;
    if (s.year === e.year) return `${s.day}/${s.month} - ${e.day}/${e.month}/${e.year}`;
    return `${s.day}/${s.month}/${s.year} - ${e.day}/${e.month}/${e.year}`;
  }
  if (startDate) { const s = fmtPart(startDate); return `A partir de ${s.day}/${s.month}/${s.year}`; }
  const e = fmtPart(endDate); return `Ate ${e.day}/${e.month}/${e.year}`;
};

/**
 * Formata `numeroCompra` adicionando o ano só quando ainda não está embutido.
 *
 * Casos que NÃO duplica o ano:
 *   - "0019/2026"  → "0019/2026"     (já tem /AAAA no fim)
 *   - "PE-078-2026" → "PE-078-2026"  (já tem -AAAA no fim)
 *   - "90014/2025" + ano=2026 → "90014/2025"  (respeita ano embutido)
 *
 * Casos que adiciona:
 *   - "18" + ano=2026 → "18/2026"
 *   - "0019" + ano=2026 → "0019/2026"
 *
 * Bug visto antes (23/05/2026): "PE-078-2026/2026" porque a regex só
 * detectava barra. Caso real: BLL retorna numero com sufixo de ano via hífen.
 */
export const formatNumeroCompraComAno = (
  numeroCompra: string | null | undefined,
  anoCompra: number | string | null | undefined,
): string => {
  if (!numeroCompra) return '—';
  const num = String(numeroCompra).trim();
  // Só dígitos, um espaço e o ano ("136301 2026", como a BNC grava, sem anoCompra): vira "136301/2026", a forma de
  // todo o resto (15/10/2026). Estreito de propósito: "10 de 2026" e "PR 033/2026-PMO" não casam e ficam como estão.
  const digitosEAno = /^(\d+)\s+((?:19|20)\d{2})$/.exec(num);
  if (digitosEAno) return `${digitosEAno[1]}/${digitosEAno[2]}`;
  // Já termina com /AAAA ou -AAAA (qualquer 4 dígitos) → não duplica
  if (/[/-]\d{4}$/.test(num)) return num;
  if (!anoCompra) return num;
  const ano = String(anoCompra).trim();
  // O ano JÁ ESTÁ no número, em outras formas que o portal usa (07/10/2026, medido em 228 propostas: 5 saíam
  // com o ano repetido, ex. "59/26/2026"). Só cola o ano quando ele não está lá:
  //  - os 4 dígitos do ano como palavra própria, em qualquer posição ("PR 033/2026-PMO", "10 de 2026"). Colado a
  //    outros dígitos não conta: "202606290" é número de compra, não ano.
  //  - termina em /AA ou -AA com os dois últimos dígitos DO ANO ("59/26", "FS000023/26"). Outros dois dígitos
  //    no fim ("59/27" num processo de 2026) não são o ano.
  if (new RegExp(`(^|\\D)${ano}(\\D|$)`).test(num)) return num;
  if (new RegExp(`[/-]${ano.slice(-2)}$`).test(num)) return num;
  // Barra final não ganha segunda barra ("42/CCP/" → "42/CCP/2026").
  return `${num.replace(/\/+$/, '')}/${ano}`;
};

/**
 * Situação de vencimento para colorir linha/badge.
 * Estava duplicado dentro do RecebiveisTab; centralizado aqui porque a regra do
 * repo é ter toda lógica de data em formatters.ts (e a tela de Boletos precisa
 * exatamente da mesma leitura de atraso).
 */
export type DueStatus = 'ok' | 'warning' | 'overdue' | 'none';

export const getDueDateStatus = (dueDate: any): DueStatus => {
  if (!dueDate) return 'none';
  const dias = getDaysUntil(dueDate);
  if (dias === null) return 'none';
  if (dias < 0) return 'overdue';
  if (dias <= 5) return 'warning';
  return 'ok';
};

/** Dias de atraso (positivo = vencido há N dias; 0 ou negativo = em dia). */
export const getDaysOverdue = (dueDate: any): number => {
  const dias = getDaysUntil(dueDate);
  return dias === null ? 0 : -dias;
};

/** Faixa de aging usada nos filtros e no resumo de vencidos. */
export const getAgingBucket = (dueDate: any): '1-7' | '8-30' | '30+' | null => {
  const atraso = getDaysOverdue(dueDate);
  if (atraso <= 0) return null;
  if (atraso <= 7) return '1-7';
  if (atraso <= 30) return '8-30';
  return '30+';
};

/**
 * Número digitado no padrão brasileiro → number. `null` quando não dá para ler.
 *
 * ⛔ Nasceu do piso da disputa (TRT-8 PE 90009/2026, 24/09/2026). O campo era
 * `<input type="number">` e o `onChange` fazia `Number(e.target.value)`. Num
 * campo numérico o navegador considera a VÍRGULA conteúdo inválido e devolve
 * string VAZIA — mas o texto digitado continua na tela, porque o React já tem
 * `''` no `value` e não toca no DOM. Resultado: o operador via "40240,8" no
 * campo e o estado guardava `null`. Três dos quatro pisos nunca entraram, e o
 * robô entrou numa disputa REAL autorizado a descer R$ 5.230,80 abaixo do que
 * o operador acreditava ser o limite dele.
 *
 * Aceita os dois separadores porque os dois chegam na prática: "40240,8",
 * "40.240,80", "316.44" e "1260". A regra é o ÚLTIMO separador mandar — é ele
 * que separa os centavos; o que vier antes é agrupamento de milhar.
 */
export const parseDecimalBr = (texto: any): number | null => {
  if (texto === null || texto === undefined) return null;
  const s = String(texto).trim().replace(/\s|R\$/g, '');
  if (!s) return null;
  if (!/^-?[\d.,]+$/.test(s)) return null;

  const ultimaVirgula = s.lastIndexOf(',');
  const ultimoPonto = s.lastIndexOf('.');
  const corte = Math.max(ultimaVirgula, ultimoPonto);

  // ⛔ "1.260" é ambíguo: milhar brasileiro (1260) ou decimal americano (1,26).
  // Sem vírgula em lugar nenhum e com EXATAMENTE 3 dígitos depois do ponto,
  // vale milhar. O desempate erra de propósito para o lado seguro: num piso,
  // ler 1260 onde era 1,26 deixa o robô conservador; ler 1,26 onde era 1260 o
  // autoriza a dar mil vezes mais desconto.
  const pontoDeMilhar =
    ultimaVirgula === -1 && ultimoPonto !== -1 && /^\d{3}$/.test(s.slice(ultimoPonto + 1));

  let normalizado: string;
  if (corte === -1 || pontoDeMilhar) {
    normalizado = s.replace(/[.,]/g, '');
  } else {
    const inteiro = s.slice(0, corte).replace(/[.,]/g, '');
    const decimais = s.slice(corte + 1);
    // Separador sozinho no fim ("1260,") conta como inteiro, não como erro.
    normalizado = decimais ? `${inteiro}.${decimais}` : inteiro;
  }

  const n = Number(normalizado);
  return Number.isFinite(n) ? n : null;
};

/**
 * Quanto de desconto um piso em reais representa sobre o valor estimado.
 * `null` quando não dá para comparar — é o que a UI mostra como "—".
 *
 * Em pregão julgado por MAIOR DESCONTO o piso continua em reais (o motor todo
 * opera no preço derivado), mas quem opera a sessão pensa em percentual. Sem
 * ver os dois lado a lado, um piso que não entrou passa batido — foi o que
 * aconteceu no TRT-8.
 */
export const descontoEquivalente = (piso: any, estimado: any): number | null => {
  const p = typeof piso === 'number' ? piso : parseDecimalBr(piso);
  const e = typeof estimado === 'number' ? estimado : parseDecimalBr(estimado);
  if (p === null || e === null || e <= 0) return null;
  return ((e - p) / e) * 100;
};
